import React, {
  ChangeEvent,
  Fragment,
  ReactNode,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { ReactComponent as UploadIcon } from 'assets/icons/upload.svg';
import { STORAGE_KEYS } from 'utils/constants';
import { toast } from 'react-toastify';
import { ToastMessage } from '@cokitchen/cokitchen-components';
import { getFileSize } from 'utils/functions';
import { DeleteIcon } from 'cokitchen-icons';

type Props = {
  label?: ReactNode;
  setFileUrls: (images: string[]) => void;
  fileUrls?: string[];
  fileType?: 'image' | 'document';
  disabled?: boolean;
  isMultiple?: boolean;
  className?: string;
  onUploadStatus?: (uploading: boolean) => void;
};
const FileUploader = ({ fileType = 'image', fileUrls, setFileUrls, onUploadStatus }: Props) => {
  const fileUploadRef = useRef<HTMLInputElement | null>(null);
  const [hasUploaded, setHasUploaded] = useState(false);
  const [allowDrop, setAllowDrop] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [files, setFiles] = useState<File[] | null>(null);
  const [percent, setPercent] = useState(0);
  const token = localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN) as string;
  const [exceedSize, setExceedSize] = useState(false);
  const MAX_SIZE = 5;

  const onFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    setHasUploaded(false);
    const { files } = e.target;
    if (files) {
      if (files[0].size <= MAX_SIZE * 1024 * 1024) {
        setFiles([files[0]]);
        setExceedSize(false);
      } else {
        setExceedSize(true);
      }
    }
  };

  const uploadSingleFile = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const numberOfFiles = files?.length ?? 1;
      const formData = new FormData();
      formData.append('file', file);

      const xhr = new XMLHttpRequest();
      xhr.open('POST', 'https://file-api.getfoodcourt.com/upload');
      xhr.setRequestHeader('Authorization', `Bearer ${token}`);

      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable) {
          const progress = Math.round((event.loaded * (100 / numberOfFiles)) / event.total);
          setPercent(progress);
        }
      };
      xhr.onload = async () => {
        const data = JSON.parse(xhr.responseText);
        if (data) {
          resolve(data.url);
        }
      };

      xhr.onerror = () => {
        reject(Error('Problem uploading image. Contact tech support'));
      };

      xhr.send(formData);
    });
  };

  const handleDragOver = (e: any) => {
    if (allowDrop) e.preventDefault();
  };
  const handleFileDrop = (e: any) => {
    if (allowDrop) {
      e.preventDefault();
      const files = e.dataTransfer.files;
      if (files) {
        setFiles([files[0]]);
      }
    }
  };

  const uploadImg = async () => {
    if (!files) return;
    setUploading(true);
    setPercent(0);
    try {
      onUploadStatus?.(true);
      const response = await uploadSingleFile(files[0]);
      setFileUrls([response]);
      setHasUploaded(true);
      setPercent(100);
    } catch {
      setFiles([]);
      toast(<ToastMessage type='error' message='Problem encountered while uploading images' />);
    } finally {
      setUploading(false);
      onUploadStatus?.(false);
    }
  };

  const getImgSrc = useMemo(() => {
    if (files?.length) {
      setUploading(true);
      uploadImg();
      onUploadStatus?.(true);
      return URL.createObjectURL(files[0]);
    }
    return '';
  }, [files]);

  const removeImage = () => {
    setFiles([]);
    setFileUrls([]);
    setHasUploaded(false);
    setUploading(false);
  };

  useEffect(() => {
    if (uploading) {
      setAllowDrop(false);
    } else {
      setPercent(0);
      setAllowDrop(true);
    }
  }, [uploading]);
  useEffect(() => {
    if (percent === 100) {
      setUploading(false);
    }
  }, [percent, uploading]);
  return (
    <div>
      <div
        style={{
          backgroundImage: fileType === 'image' ? `url(${fileUrls?.[0]})` : 'none',
          backgroundPosition: 'center',
          backgroundSize: 'cover',
          backgroundRepeat: 'no-repeat'
        }}
        className='bg-[#FAFAFA] border-b-2 border-[#D5D6D7] py-[19px] cursor-pointer'
        onDrop={handleFileDrop}
        onDragOver={handleDragOver}
        onClick={() => {
          if (!uploading) fileUploadRef?.current?.click();
        }}>
        <div className='flex justify-center'>
          <div className='text-center text-xs text-[#6F7376] h-[120px]'>
            {hasUploaded && fileUrls?.length ? (
              <div className='w-full h-full flex justify-center items-center'>
                <p className='font-medium text-[#fff] text-base'>Update image</p>
              </div>
            ) : (
              <Fragment>
                <div className='flex justify-center'>
                  <UploadIcon />
                </div>

                <p className='mt-1  font-medium'>
                  <span className='text-[#2E3031] underline cursor-pointer'>Click to upload</span>
                  <span className='ml-1'>or drag and drop</span>
                </p>
                <p className='mt-1'>SVG, PNG or JPG (Max. 500x500px)</p>
              </Fragment>
            )}
          </div>
        </div>
        <input ref={fileUploadRef} type='file' accept='image/*' hidden onChange={onFileChange} />
      </div>
      {exceedSize && <div className='mt-2 text-red-5 text-sm'>Image size exceeds 5mb</div>}
      {files?.length ? (
        <div className='file-uploader_multiple'>
          <div className='!p-3 file-uploader_file'>
            <div
              className='file-uploader_file_main'
              style={{
                width: files.length > 1 ? '80%' : '100%'
              }}>
              <div className='file-uploader_file_main_inner'>
                <div
                  style={{
                    backgroundImage: fileType === 'image' ? `url(${getImgSrc})` : 'none',
                    backgroundSize: 'cover',
                    backgroundPosition: 'center'
                  }}>
                  {fileType !== 'image' && <span style={{ fontSize: '2rem' }}>📄</span>}
                </div>
              </div>
              <div className='duration_box'>
                {/* <label>{files?.[0].name}</label> */}
                <div className='text'>
                  <span>{getFileSize(files?.[0].size)}</span>
                  <div />
                  {uploading ? <span>Uploading...</span> : <span>Upload Complete</span>}
                </div>
              </div>
              {hasUploaded && files.length === 1 && (
                <div role='button' onClick={removeImage} className='has_uploaded'>
                  <DeleteIcon />
                </div>
              )}
              {uploading && <span className='uploading_text'>{percent}%</span>}
            </div>

            {uploading && (
              <div className='is_uploading'>
                <div
                  style={{
                    height: '100%',
                    width: `${percent}%`,
                    transition: 'width .8s'
                  }}
                />
              </div>
            )}
          </div>
        </div>
      ) : (
        <span />
      )}
    </div>
  );
};
export default FileUploader;
